import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { adminApi, type PlaybackHealthRow } from '@/services/adminApi';
import { SOURCE_NAMES, type SourceName } from '@/types/streaming';
import { formatRelativeTime } from '@/i18n/relativeTime';

export function PlaybackHealthSection(): JSX.Element {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Record<SourceName, PlaybackHealthRow | null>>(() =>
    SOURCE_NAMES.reduce<Record<SourceName, PlaybackHealthRow | null>>((acc, n) => {
      acc[n] = null;
      return acc;
    }, {} as Record<SourceName, PlaybackHealthRow | null>),
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    adminApi.listPlaybackHealth()
      .then(({ rows: fetched }) => {
        if (cancelled) return;
        setRows((prev) => {
          const next = { ...prev };
          for (const r of fetched) next[r.providerName] = r;
          return next;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : t('admin.playbackHealth.loadError'));
      });
    return (): void => { cancelled = true; };
  }, []);

  return (
    <section className="admin-card" data-testid="playback-health-section">
      <div className="admin-card__row">
        <h2 className="admin-card__title">{t('admin.playbackHealth.title')}</h2>
        <span className="muted">{t('admin.playbackHealth.cronNote')}</span>
      </div>
      <p className="muted">
        <Trans i18nKey="admin.playbackHealth.blurb" components={{ code: <code /> }} values={{ videoTag: '<video>' }} />
      </p>
      {loadError && <p className="error" role="alert">{loadError}</p>}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col" aria-label={t('admin.common.status')} />
              <th scope="col">{t('admin.common.source')}</th>
              <th scope="col">{t('admin.playbackHealth.colStage')}</th>
              <th scope="col" className="num">{t('admin.playbackHealth.colFirstFrame')}</th>
              <th scope="col">{t('admin.playbackHealth.colLastProbe')}</th>
              <th scope="col">{t('admin.playbackHealth.colNotes')}</th>
              <th scope="col">{t('admin.playbackHealth.colArtifacts')}</th>
            </tr>
          </thead>
          <tbody>
            {SOURCE_NAMES.map((name) => {
              const r = rows[name];
              const state: 'ok' | 'down' | 'unknown' = r === null ? 'unknown' : r.ok ? 'ok' : 'down';
              return (
                <tr key={name}>
                  <td><span className={`health-dot health-dot--${state}`} role="img" aria-label={state} /></td>
                  <td><code>{name}</code></td>
                  <td>{r ? r.stage : <span className="text-dim">—</span>}</td>
                  <td className="num">{r && r.firstFrameMs !== null ? `${r.firstFrameMs} ms` : <span className="text-dim">—</span>}</td>
                  <td>{r ? formatRelativeTime(t, r.ranAt, nowMs) : <span className="text-dim">{t('common.relativeTime.never')}</span>}</td>
                  <td>{r && r.message ? r.message : <span className="text-dim">—</span>}</td>
                  <td>
                    {r && r.artifactUrl
                      ? <a href={r.artifactUrl} target="_blank" rel="noopener noreferrer">{t('admin.playbackHealth.screenshots')}</a>
                      : <span className="text-dim">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
